import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Use a singleton pattern to ensure only one client instance exists
// This prevents "Multiple GoTrueClient instances" warnings and session conflicts
let supabaseClient: SupabaseClient | null = null;

function getSupabaseClient(): SupabaseClient | null {
  if (supabaseClient) return supabaseClient;
  
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Missing Supabase environment variables. Auth will not work.');
    return null;
  }
  
  // Check if window already has a client (for HMR scenarios)
  if (typeof window !== 'undefined' && (window as any).__supabaseClient) {
    supabaseClient = (window as any).__supabaseClient;
    return supabaseClient;
  }
  
  supabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      storageKey: 'myFitBeats-auth',
      flowType: 'pkce',
    },
  });
  
  // Store on window for HMR
  if (typeof window !== 'undefined') {
    (window as any).__supabaseClient = supabaseClient;
  }
  
  return supabaseClient;
}

supabaseClient = getSupabaseClient();

export const supabase = supabaseClient as SupabaseClient;

export async function getAuthToken(): Promise<string | null> {
  if (!supabase) return null;
  
  const { data: { session }, error } = await supabase.auth.getSession();
  
  if (error) {
    console.warn('[supabase] Error getting session:', error.message);
    // Check if localStorage has auth data despite error
    const storedAuth = localStorage.getItem('myFitBeats-auth');
    if (storedAuth) {
      console.warn('[supabase] Session error but auth data exists in localStorage');
    }
    return null;
  }
  
  if (!session?.access_token) {
    // Debug: Check if there's stale auth data
    const storedAuth = localStorage.getItem('myFitBeats-auth');
    if (storedAuth) {
      console.warn('[supabase] No session but auth data exists in localStorage - may need refresh');
    }
    return null;
  }
  
  // Check if token is about to expire (within 60 seconds)
  // Proactively refresh to avoid mid-request expiration
  const expiresAt = session.expires_at;
  if (expiresAt) {
    const nowSeconds = Math.floor(Date.now() / 1000);
    const timeUntilExpiry = expiresAt - nowSeconds;
    
    if (timeUntilExpiry < 60) {
      console.log('[supabase] Token expiring soon, refreshing...');
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.warn('[supabase] Failed to refresh token:', refreshError.message);
        // Return current token - it might still work for a few more seconds
        return session.access_token;
      }
      
      if (refreshData.session?.access_token) {
        return refreshData.session.access_token;
      }
    }
  }
  
  return session.access_token;
}
